<template>
    <div class="ui-page">
        <ui-page-header :title="page.title" :back="backLink.to" :back-text="backLink.text">
            <v-btn id="btnSave" type="button" color="primary" outlined @click="finalizeAnalysis"
                :loading="dataForm.addLoading" v-if="(!page.isNew) && $allowed('edit')">Finalizar</v-btn>
            <v-btn id="btnSave" type="button" color="primary" depressed @click="add"
                :loading="dataForm.addLoading" v-if="(page.isNew) && $allowed('letter.create')">Salvar</v-btn>
            <v-btn id="btnSave" type="button" color="primary" depressed @click="update"
                :loading="dataForm.updateLoading" v-if="(!page.isNew) && $allowed('save')">Salvar</v-btn>
        </ui-page-header>

        <v-form :readonly="!$allowed('save')" ref="form" v-model="dataForm.validForm">
            <!-- Credor -->
            <ui-section title="Credor">
                <v-row>
                    <v-col cols="12" md="5">
                        <v-autocomplete dense :items="dataset.clients" label="Caso"
                            v-model="selectedClientId" item-value="id" item-text="name"
                            outlined hide-details="auto" :rules="[$rules.required]"
                            no-data-text="Nenhum caso encontrado">
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="7">
                        <v-autocomplete dense
                            v-model="selectedCreditorId"
                            :items="creditorOptions"
                            :search-input.sync="creditorSearch"
                            :loading="creditorSearchLoading"
                            :disabled="!selectedClientId"
                            :rules="[creditorRequired]"
                            label="Pesquisar credor por nome, CPF/CNPJ ou caso"
                            item-value="id"
                            return-object
                            outlined
                            hide-details="auto"
                            clearable
                            no-filter
                            no-data-text="Nenhum credor encontrado"
                            @change="selectCreditor"
                        >
                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ formatRegistration(item.registration) }} - {{ getCreditorTypeName(item.type) }}
                                        <span v-if="item.client && item.client.name"> - {{ item.client.name }}</span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                            <template v-slot:selection="{ item }">
                                <span>{{ item.name }} - {{ formatRegistration(item.registration) }} - {{ getCreditorTypeName(item.type) }}</span>
                            </template>
                            <template v-slot:no-data>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ selectedClientId ? 'Nenhum credor encontrado' : 'Selecione o caso antes de pesquisar o credor' }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item v-if="selectedClientId">
                                    <v-btn v-if="$allowed('create')" small color="primary" outlined @click.stop="openAddCreditorDialog">
                                        <v-icon left small>mdi-account-plus</v-icon>
                                        Adicionar credor
                                    </v-btn>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>

                <v-alert v-if="dataset.modalResult.name" type="info" outlined dense class="mt-4 mb-0">
                    Credor selecionado: <strong>{{ dataset.modalResult.name }}</strong>
                    - {{ formatRegistration(dataset.modalResult.registration) }}
                    - {{ getCreditorTypeName(dataset.modalResult.type) }}
                </v-alert>
            </ui-section>

            <!-- Detalhes -->
            <ui-section title="Detalhes">
                <v-row>
                    <v-col cols="12" md="4">
                        <v-currency-field label="Valor" dense v-model="dataset.data.amount"
                            :allowNegative=false hide-details="auto" outlined required
                            :rules="[value => value !== null && value !== undefined && value !== '' || 'Campo obrigatório']">
                        </v-currency-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-select dense :items="dataset.creditorTypes" label="Classe"
                            v-model="dataset.modalResult.type" item-value="id" item-text="name"
                            outlined hide-details="auto" :disabled="!dataset.modalResult.name"
                            :rules="[$rules.required]" :menu-props="{ offsetY: true }">
                        </v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field dense type="date" label="Prazo contraditório"
                            :value="formatDate(dataset.data.contradictory_deadline_at)"
                            @input="updateContraditory" outlined hide-details="auto">
                        </v-text-field>
                    </v-col>
                </v-row>
            </ui-section>

            <!-- Analise (antes era um cartao dentro de Detalhes) -->
            <ui-section title="Análise">
                <template #actions>
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <v-btn v-on="on" small text color="primary" @click="openDialogDynamicText(false)">
                                <v-icon left small>mdi-text-long</v-icon>
                                Textos predefinidos
                            </v-btn>
                        </template>
                        <span>Selecionar textos longos predefinidos</span>
                    </v-tooltip>
                </template>
                <ui-document-editor ref="reasonEditor" v-model="dataset.data.reason" title="Análise" orientation="landscape" :min-height="320" placeholder="Texto da análise" />
            </ui-section>
        </v-form>

        <v-dialog v-model="addCreditorDialog.show" max-width="700px" persistent>
            <div class="ui-card">
                <header class="ui-card__header">
                    <h3 class="ui-section-title">Adicionar credor</h3>
                </header>
                <div class="ui-card__body">
                    <v-form :readonly="!$allowed('save')" ref="addCreditorForm" v-model="addCreditorDialog.valid">
                        <v-row>
                            <v-col cols="12">
                                <v-alert type="info" outlined dense class="mb-0">
                                    O novo credor será vinculado ao caso selecionado.
                                </v-alert>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field dense label="Nome completo" v-model="addCreditorDialog.form.name"
                                    outlined hide-details="auto" :rules="[$rules.required]">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field dense label="CPF/CNPJ" v-model="addCreditorDialog.form.registration"
                                    outlined hide-details="auto" :rules="[$rules.required]"
                                    @input="formatAddCreditorRegistration">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-select dense :items="dataset.creditorTypes" label="Classe"
                                    v-model="addCreditorDialog.form.type" item-value="id" item-text="name"
                                    outlined hide-details="auto" :rules="[$rules.required]" :menu-props="{ offsetY: true }">
                                </v-select>
                            </v-col>
                        </v-row>
                    </v-form>
                </div>
                <footer class="ui-card__footer">
                    <v-btn text @click="closeAddCreditorDialog">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="addCreditorDialog.loading" @click="confirmAddCreditor">Adicionar</v-btn>
                </footer>
            </div>
        </v-dialog>

        <v-dialog v-model="dialogDynamicText" :width="900">
            <div class="ui-card">
                <header class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Textos Automáticos</h3>
                        <p class="ui-section-description">Selecione um texto abaixo para adicionar na análise</p>
                    </div>
                </header>
                <div class="ui-card__body" style="max-height: 560px; overflow-y: auto;">
                    <v-list-item v-for="(item, index) in dataset.dynamicTexts" :key="index"
                        style="cursor: pointer" @click="selectDynamicText(item)">
                        <v-list-item-content>
                            <v-list-item-title class="font-weight-medium">{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>
                                <div v-html="dynamicTextSmallType ? item.small_description : item.description"></div>
                                <v-divider></v-divider>
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </div>
                <footer class="ui-card__footer">
                    <v-btn type="button" text @click="dialogDynamicText = false;">Cancelar</v-btn>
                </footer>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios"
import creditorTypes from "@/common/data/creditor-types.data"
import creditorClassUtil from "@/common/util/creditor-class.util"
import { cpf, cnpj } from 'cpf-cnpj-validator'
import _ from 'lodash'

export default {
    name: "LetterAnalysisDetails",

    components: {
    },

    data: () => ({
        page: {
            isNew: false,
            title: "",
        },
        creditorMode: 'existing',
        selectedClientId: null,
        loadingExistingLetter: false,
        selectedCreditorId: null,
        creditorSearch: '',
        creditorSearchItems: [],
        creditorSearchLoading: false,
        newCreditor: {
            client_id: null,
            client_company_id: null,
            name: '',
            registration: '',
            type: null,
            currency: 1,
            version: 1,
        },
        addCreditorDialog: {
            show: false,
            valid: true,
            loading: false,
            form: {
                name: '',
                registration: '',
                type: null,
            },
        },
        dataset: {
            breadcrumbItems: [],
            dynamicTexts: [],
            clients: [],
            creditorTypes: creditorTypes,
            data: {
                contradictory_deadline_at: null,
                credit_analysis_key: null,
            },
            modalResult: {
                name: null,
                registration: null,
                type: null,
                client: { name: null }
            },
        },
        dynamicTextSmallType: false,
        dialogDynamicText: false,
        dataForm: {
            validForm: true,
            addLoading: false,
            updateLoading: false,
            removeLoading: false,
        },
    }),

    computed: {
        // Mesmo destino do goBack(): volta para a Central quando veio de la
        backLink() {
            if (this.$route.query.from_central === "1") {
                return {
                    to: { path: "/central-analise-creditos", query: { client_id: this.$route.query.client_id } },
                    text: "Central de Créditos",
                };
            }
            if (this.$route.query.from_list === "1" && this.$route.query.client_id) {
                return { to: { path: "/analise-oficio", query: { client_id: this.$route.query.client_id } }, text: "Análise de Ofício" };
            }
            return { to: "/analise-oficio", text: "Análise de Ofício" };
        },

        creditorOptions() {
            const selected = this.dataset.modalResult && this.dataset.modalResult.id
                ? [this.dataset.modalResult]
                : [];

            const options = [...selected, ...this.creditorSearchItems];
            return options.filter((item, index, list) => {
                return item && item.id && list.findIndex((option) => Number(option.id) === Number(item.id)) === index;
            });
        },

        selectedClient() {
            return this.dataset.clients.find(item => Number(item.id) === Number(this.selectedClientId)) || { name: null };
        },
    },

    watch: {
        selectedClientId() {
            // Ao carregar um oficio existente o caso e preenchido pelo load, e
            // resetar aqui apagaria o credor e a classe recem-carregados. O
            // reset so faz sentido quando a troca parte do usuario.
            if (!this.loadingExistingLetter) this.resetCreditorSelection();

            this.loadCreditorClasses();
        },

        creditorSearch(value) {
            this.debouncedLoadCreditors(value);
        },
    },

    async created() {
        await Promise.all([
            this.loadData(),
            this.loadClients(),
            this.loadCreditorClasses(),
        ]);
        await this.prefillFromCentral();
    },

    methods: {
        async loadCreditorClasses() {
            // Inclui ocultas: creditos ja analisados mantem o nome da classe.
            this.dataset.creditorTypes = await creditorClassUtil.listLabels(this.selectedClientId);
        },

        goBack() {
            if (this.$route.query.from_central === "1") {
                this.$router.replace({
                    path: "/central-analise-creditos",
                    query: { client_id: this.$route.query.client_id },
                });
                return;
            }
            if (this.$route.query.from_list === "1" && this.$route.query.client_id) {
                this.$router.replace({ path: "/analise-oficio", query: { client_id: this.$route.query.client_id } });
                return;
            }
            this.$router.replace("/analise-oficio");
        },

        async loadData() {
            this.setPage();
            this.listBreadCrumbItems();
            await this.load();
        },

        async loadClients() {
            try {
                const result = await axios.get('/api/clients', { params: { start: 1, limit: 100000, search: {} } });
                this.dataset.clients = result.data.content.data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        debouncedLoadCreditors: _.debounce(function (value) {
            this.loadCreditors(value);
        }, 400),

        async loadCreditors(value = '') {
            if (!this.selectedClientId) {
                this.creditorSearchItems = [];
                return;
            }

            const term = String(value || '').trim();

            if (term.length < 2) {
                this.creditorSearchItems = [];
                return;
            }

            this.creditorSearchLoading = true;

            try {
                const result = await axios.get('/api/admin/users-creditors', {
                    params: {
                        page: 1,
                        limit: 20,
                        search: {
                            client_id: this.selectedClientId,
                            term,
                        }
                    }
                });

                this.creditorSearchItems = result.data.content.data || [];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            } finally {
                this.creditorSearchLoading = false;
            }
        },

        creditorRequired() {
            return !!this.dataset.modalResult.name || "Selecione ou adicione um credor.";
        },

        selectCreditor(item) {
            if (!item) {
                this.resetCreditorSelection(false);
                return;
            }

            this.creditorMode = 'existing';
            this.newCreditor = {
                client_id: null,
                client_company_id: null,
                name: '',
                registration: '',
                type: null,
                currency: 1,
                version: 1,
            };
            this.dataset.modalResult = {
                ...item,
                client: item.client || this.selectedClient,
            };
        },

        resetCreditorSelection(clearSearch = true) {
            this.selectedCreditorId = null;
            this.creditorSearchItems = [];
            this.dataset.modalResult = {
                name: null,
                registration: null,
                type: null,
                client: { name: null }
            };

            if (clearSearch) this.creditorSearch = '';
        },

        openAddCreditorDialog() {
            if (!this.selectedClientId) {
                this.$dialog.notify.error("Selecione o caso antes de adicionar um credor.", { position: "top-right", timeout: 3000 });
                return;
            }

            this.addCreditorDialog.form = {
                name: '',
                registration: '',
                type: null,
            };
            this.addCreditorDialog.show = true;
        },

        closeAddCreditorDialog() {
            this.addCreditorDialog.show = false;
        },

        confirmAddCreditor() {
            if (!this.$refs.addCreditorForm.validate()) return;

            const form = this.addCreditorDialog.form;
            this.creditorMode = 'new';
            this.newCreditor = {
                client_id: this.selectedClientId,
                client_company_id: null,
                name: form.name,
                registration: this.onlyDigits(form.registration),
                type: form.type,
                currency: 1,
                version: 1,
            };

            this.selectedCreditorId = null;
            this.dataset.modalResult = {
                id: null,
                name: form.name,
                registration: this.onlyDigits(form.registration),
                type: form.type,
                client: this.selectedClient,
            };
            this.addCreditorDialog.show = false;
        },

        onlyDigits(value) {
            return String(value || '').replace(/\D/g, '');
        },

        formatRegistration(value) {
            const digits = this.onlyDigits(value);
            if (!digits) return 'N/A';
            return digits.length > 11 ? cnpj.format(digits.slice(0, 14)) : cpf.format(digits.slice(0, 11));
        },

        formatAddCreditorRegistration() {
            this.addCreditorDialog.form.registration = this.formatRegistration(this.addCreditorDialog.form.registration);
        },

        async prefillFromCentral() {
            if (!this.page.isNew || (this.$route.query.from_central !== "1" && this.$route.query.from_list !== "1")) return;

            const query = this.$route.query;
            this.selectedClientId = query.client_id ? Number(query.client_id) : null;
            this.dataset.data.amount = query.amount !== undefined ? Number(query.amount) : null;
            this.dataset.data.credit_analysis_key = query.credit_key || null;
            await this.$nextTick();

            if (query.user_creditor_id) {
                try {
                    const result = await axios.get(`/api/admin/users-creditors/${query.user_creditor_id}`);
                    const creditor = result.data.content;
                    this.selectedCreditorId = creditor;
                    this.selectCreditor(creditor);
                    return;
                } catch (error) {
                    this.$dialog.notify.warning(
                        "O credor do QGC não foi localizado. Os dados foram preparados para um novo vínculo.",
                        { position: "top-right", timeout: 4000 }
                    );
                }
            }

            this.creditorMode = "new";
            this.newCreditor = {
                client_id: this.selectedClientId,
                client_company_id: query.client_company_id ? Number(query.client_company_id) : null,
                name: query.creditor_name || "",
                registration: this.onlyDigits(query.registration),
                type: query.creditor_type ? Number(query.creditor_type) : null,
                currency: query.currency ? Number(query.currency) : 1,
                version: 3,
            };
            this.dataset.modalResult = {
                id: null,
                name: this.newCreditor.name,
                registration: this.newCreditor.registration,
                type: this.newCreditor.type,
                client_company_id: this.newCreditor.client_company_id,
                currency: this.newCreditor.currency,
                client: this.selectedClient,
            };
        },

        getCreditorTypeName(typeId) {
            const type = this.dataset.creditorTypes.find(item => Number(item.id) === Number(typeId));
            return type ? type.name : 'N/A';
        },

        openDialogDynamicText(small = false) {
            this.loadDynamicTexts()
            this.dynamicTextSmallType = small;
            this.dialogDynamicText = true;
        },

        formatDate(date) {
            if (!date || date === "0000-00-00 00:00:00") return null;
            // Prazo e data civil de Brasilia: usa o dia gravado, sem conversao de fuso.
            return String(date).slice(0, 10);
        },

        updateContraditory(value) {
            this.dataset.data.contradictory_deadline_at = value;
        },

        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = `${this.page.isNew ? 'Nova análise' : 'Detalhes da análise'}`;
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                { text: "Homepage", disabled: false, to: "/home", exact: true },
                { text: "Análise de ofício", disabled: false, to: "/analise-oficio", exact: true },
                { text: this.page.title, disabled: true, exact: true },
            ];
        },

        async loadDynamicTexts() {
            try {
                const result = await axios.get(`/api/admin/dynamic-texts`, { params: { start: 1, limit: 100000, search: {} } });
                this.dataset.dynamicTexts = result.data.content.data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        selectDynamicText(item) {
            if (this.dynamicTextSmallType) {
                this.dataset.data.smallReason += item.small_description;
            } else if (this.$refs.reasonEditor) {
                // Insere onde esta o cursor do editor.
                this.$refs.reasonEditor.insertHtml(item.description || "");
            } else {
                this.dataset.data.reason += item.description;
            }
            this.dialogDynamicText = false;
        },

        notifyEditorError(message) {
            this.$dialog.notify.error(message, { position: 'top-right', timeout: 3000 });
        },

        async load() {
            if (this.page.isNew) return;

            try {
                const result = await axios.get(`/api/admin/official-letters/${this.$route.params.id}`);
                const data = result.data.content;
                this.dataset.data = data;

                if (data && data.userCreditor) {
                    this.loadingExistingLetter = true;
                    this.selectedClientId = data.userCreditor.client_id;
                    this.selectedCreditorId = data.userCreditor;
                    this.dataset.modalResult = {
                        ...data.userCreditor,
                        client: data.userCreditor.client || { name: null },
                    };
                    this.$nextTick(() => { this.loadingExistingLetter = false; });

                    const client_id = data.userCreditor.client_id;
                    try {
                        const clientResult = await axios.get(`/api/clients/${client_id}`);
                        this.dataset.modalResult.client = { name: clientResult.data.content.name };
                    } catch (error) {
                        // client not found
                    }
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        async add() {
            if (!this.$refs.form.validate()) return;

            if (!this.selectedClientId) {
                this.$dialog.notify.error("Selecione o caso antes de salvar a análise.", { position: "top-right", timeout: 3000 });
                return;
            }

            if (!this.dataset.modalResult.name) {
                this.$dialog.notify.error("Selecione ou adicione um credor.", { position: "top-right", timeout: 3000 });
                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja incluir a análise?',
                title: 'Incluir análise',
                actions: { false: 'Cancelar', true: 'Salvar' }
            });

            if (!proceedAction) return;

            this.dataForm.addLoading = true;

            const payload = this.buildPayload();

            try {
                await axios.post('/api/admin/official-letters', payload);
                this.$dialog.notify.info("Análise salva com sucesso!", { position: "top-right", timeout: 2000 });
                this.goBack();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            } finally {
                this.dataForm.addLoading = false;
            }
        },

        async finalizeAnalysis() {
            if (!this.$refs.form.validate()) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja finalizar a análise?',
                title: 'Finalizar análise',
                actions: { false: 'Cancelar', true: 'Finalizar' }
            });

            if (!proceedAction) return;
        },

        async update() {
            if (!this.$refs.form.validate()) return;

            if (!this.selectedClientId) {
                this.$dialog.notify.error("Selecione o caso antes de atualizar a análise.", { position: "top-right", timeout: 3000 });
                return;
            }

            if (!this.dataset.modalResult.name) {
                this.$dialog.notify.error("Selecione ou adicione um credor.", { position: "top-right", timeout: 3000 });
                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja atualizar dados da análise?',
                title: 'Atualizar análise',
                actions: { false: 'Cancelar', true: 'Atualizar' }
            });

            if (!proceedAction) return;

            this.dataForm.addLoading = true;

            const payload = this.buildPayload();

            try {
                await axios.patch(`/api/admin/official-letters/${this.$route.params.id}`, payload);
                this.$dialog.notify.info("Análise atualizada com sucesso!", { position: "top-right", timeout: 2000 });
                this.goBack();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            } finally {
                this.dataForm.addLoading = false;
            }
        },

        buildPayload() {
            const formData = new FormData();

            if (this.dataset.data.contradictory_deadline_at)
                formData.append('contradictory_deadline_at', this.dataset.data.contradictory_deadline_at);

            if (this.dataset.data.amount || this.dataset.data.amount === 0)
                formData.append('amount', this.dataset.data.amount);

            if (this.dataset.data.reason)
                formData.append('reason', this.dataset.data.reason);

            if (this.dataset.data.credit_analysis_key)
                formData.append('credit_analysis_key', this.dataset.data.credit_analysis_key);

            if (this.creditorMode === 'existing' && this.dataset.modalResult.id) {
                formData.append('user_creditor_id', this.dataset.modalResult.id);
                formData.append('user_creditor_type', this.dataset.modalResult.type);
            }

            if (this.creditorMode === 'new') {
                formData.append('new_creditor_name', this.newCreditor.name);
                formData.append('new_creditor_registration', this.newCreditor.registration);
                formData.append('new_creditor_type', this.newCreditor.type);
                formData.append('new_creditor_client_id', this.newCreditor.client_id);
                if (this.newCreditor.client_company_id)
                    formData.append('new_creditor_client_company_id', this.newCreditor.client_company_id);
                formData.append('new_creditor_currency', this.newCreditor.currency || 1);
                formData.append('new_creditor_version', this.newCreditor.version || 1);
            }

            return formData;
        },
    },
};
</script>

<style lang="scss">
.btn-toggle-gap .v-btn {
    margin-right: 8px !important;
}

</style>
